import $ from "jquery";
import convertToBudgetFormat from "@bd-utils/convertToBudgetFormat";
import Budget from "./budget";
import Recap from "./recap";
import { updateAttributes } from "@bd-core/firebase-actions";
import { updateRoomSpending } from "@bd-stores/game-progress";
import { getState, store } from "@bd-stores/store";
import CONFIG from "@bd-core/config";

export default class Feedbacks {
    constructor() {
        this.elms = {
            nextButton: ".js-next-feedback",
            feedbacksMark: ".js-feedbacks-mark",
            feedbacks: ".js-feedbacks",
            feedbackItem: ".js-feedback-feed",
            exclamation: ".exclamation",
            finishfeedback: ".js-finish-feedback",
            feedbackPopup: ".js-feedback-popup",
            choices: ".choices",
            feedbackTitle: ".popup__feedback-title",
            feedbackSubtitle: ".popup__feedback-subtitle",
            choicesTitle: ".choices__title",
            explanation: ".explanation",
            budget: ".consequence__main",
            meterRisk: ".meter-risk",
            meterTime: ".meter-time",
            incidentsMark: ".incidents__mark",
            choicesItem: ".choices__item",
        };

        this.budget = new Budget();
        this.recap = new Recap();
        this.spending = 0;
        this.isLoading = false;
        this.index = 0;
        this.questionId = null;
        this.roomData = null;
        this.currentRoomId = null;
        this.answerId = null;
        this.currentFeedbackQuestions = [];
        this.spendingByQuestion = {};
    }

    /**
     * Get the localStorage key for shown feedbacks based on game progress ID
     * @returns {string} - The localStorage key for this game session
     */
    getFeedbacksStorageKey() {
        const gameProgressId = CONFIG.game.id;
        return `shownFeedbacks_${gameProgressId}`;
    }

    /**
     * Check if feedback popup for a specific question ID has been shown
     * @param {string} questionId - The question ID to check
     * @returns {boolean} - True if feedback has been shown, false otherwise
     */
    hasFeedbackBeenShown(questionId) {
        try {
            const storageKey = this.getFeedbacksStorageKey();
            const shownFeedbacks = localStorage.getItem(storageKey);
            if (!shownFeedbacks) return false;

            const shownFeedbacksArray = JSON.parse(shownFeedbacks);
            return shownFeedbacksArray.includes(String(questionId));
        } catch (error) {
            console.error("Error checking shown feedbacks:", error);
            return false;
        }
    }

    /**
     * Mark feedback popup for a specific question ID as shown
     * @param {string} questionId - The question ID to mark as shown
     */
    markFeedbackAsShown(questionId) {
        try {
            const storageKey = this.getFeedbacksStorageKey();
            let shownFeedbacks = localStorage.getItem(storageKey);
            let shownFeedbacksArray = shownFeedbacks
                ? JSON.parse(shownFeedbacks)
                : [];

            if (!shownFeedbacksArray.includes(String(questionId))) {
                shownFeedbacksArray.push(String(questionId));
                localStorage.setItem(
                    storageKey,
                    JSON.stringify(shownFeedbacksArray),
                );
            }
        } catch (error) {
            console.error("Error marking feedback as shown:", error);
        }
    }

    init(roomData) {
        this.roomData = roomData;
        this.currentRoomId = roomData.room_id || roomData.id;
        const totalQuestions = roomData?.questions?.length ?? 0;

        if (totalQuestions === 0) return;
        else if (totalQuestions === 1) {
            $(this.elms.nextButton).prop("disabled", true);
            $(this.elms.finishfeedback).prop("disabled", false);
        }

        const incidentTemplate = $("#feedbacks-template").html();
        const choicesTemplate = $("#choices-template").html();
        const feedbacksContainer = $(this.elms.feedbacks);

        for (let i = 0; i < totalQuestions; i++) {
            const question = roomData.questions[i];
            const index = i + 1;
            const clone = $(incidentTemplate).clone();
            clone.attr("data-id", index);
            clone.attr("data-question-id", question.question_id);
            clone.attr("id", "incident-" + index);

            const choicesContainer = clone.find(this.elms.choices);
            for (let j = 0; j < question.answers.length; j++) {
                const answer = question.answers[j];
                const feedback = answer.feedback;
                const isPositiveAnswer =
                    answer.answer_rules === "correct_answer";
                const choiceTemplate = $(choicesTemplate).clone();
                const input = choiceTemplate.find("input");
                input.attr("id", "choice-fb-" + answer.answer_id);
                input.attr("name", "question-" + index + "-choice");
                input.attr("value", answer.answer_id);
                input.attr("data-fb-id", answer.answer_id);
                choiceTemplate
                    .find(this.elms.choicesTitle)
                    .text(answer.answer_text);
                choiceTemplate
                    .find(this.elms.explanation)
                    .html(feedback.feedback_content)
                    .addClass(
                        isPositiveAnswer
                            ? "explanation--positive"
                            : "explanation--negative",
                    );
                choiceTemplate
                    .find(this.elms.budget)
                    .html(convertToBudgetFormat(feedback.action_cost));
                choiceTemplate
                    .find(this.elms.meterRisk)
                    .addClass(`meter--${feedback.feedback_risk_indicator}`);
                choiceTemplate
                    .find(this.elms.meterTime)
                    .addClass(`meter--${feedback.feedback_time_indicator}`);
                choicesContainer.append(choiceTemplate);
            }
            feedbacksContainer.append(clone);
            $(this.elms.incidentsMark).append(
                `<span class="js-feedbacks-mark${
                    index === 1 ? " is-active" : ""
                }" data-id="${index}"></span>`,
            );
        }

        this.budget.init();
        this.recap.init();

        $(this.elms.finishfeedback).on(
            "click",
            this.onfinishfeedback.bind(this),
        );
    }

    onfinishfeedback() {
        const $activeFeedback = $(".js-feedback-feed.is-active").last();
        const currentQuestionId = $activeFeedback.attr("data-question-id");
        const questionSpending =
            this.spendingByQuestion[currentQuestionId] || this.spending;

        if (currentQuestionId && this.currentRoomId && questionSpending > 0) {
            this.budget.addFeedbackBudgetForQuestion(
                questionSpending,
                this.currentRoomId,
                currentQuestionId,
            );
        }

        this.questionId = null;

        if (this.recap && this.roomData) {
            this.recap.onFinishFeedback(this.roomData, questionSpending);
        }

        $(".js-feedback-feed").removeClass("is-active");
    }

    setFeedback(questionId, answerId, roomData) {
        const elms = this.elms;
        const $feedbacks = $(elms.feedbacks);

        // if ($feedbacks.length === 0) return;

        const $feedbackItem = $feedbacks.find(
            `[data-question-id="${questionId}"]`,
        );

        if ($feedbackItem.length === 0) return;

        // Uncheck all inputs within this specific feedback item
        // $feedbackItem.find('input[data-fb-id]').prop("checked", false);
        // $feedbackItem.find(elms.choicesItem).removeClass("is-active");

        const feedback = roomData.questions
            .find((q) => q.question_id == questionId)
            .answers.find((a) => a.answer_id == answerId).feedback;

        $feedbackItem
            .find(elms.feedbackTitle)
            .text(feedback.feedback_title || "");
        $feedbackItem
            .find(elms.feedbackSubtitle)
            .text(feedback.feedback_subtitle || "");

        if (feedback) {
            const actionCost = feedback?.action_cost;
            this.spending = actionCost;
            this.spendingByQuestion[questionId] = actionCost;
        }

        const $answerInput = $feedbackItem.find(`[data-fb-id="${answerId}"]`);

        // if ($answerInput.length === 0) return;

        $feedbackItem.find(elms.choicesItem).removeClass("is-active");
        $feedbackItem
            .find("input")
            .prop("checked", false)
            .removeClass("is-active");

        $answerInput.prop("checked", true);
        $answerInput.addClass("is-active").siblings().removeClass("is-active");
        $answerInput.parents(elms.choicesItem).addClass("is-active");
    }

    saveActionCost(actionCost) {
        if (
            !this.currentRoomId ||
            actionCost === undefined ||
            actionCost === null
        ) {
            return;
        }

        store.dispatch(
            updateRoomSpending({
                roomId: this.currentRoomId,
                actionCost: Number(actionCost),
            }),
        );

        const currentSpending = getState().gameProgress.spending || {};
        updateAttributes({ spending: currentSpending });
    }

    nextfeedback() {
        const elms = this.elms;
        const $nextButton = $(elms.nextButton);
        const $items = $(elms.feedbackItem);
        // const $currentItem = $(`${elms.feedbackItem}.is-active`).attr("data-id");
        const $feedbackPopup = $(elms.feedbackPopup);
        $feedbackPopup.removeClass("is-active");

        const onCLickHandler = function () {
            this.index++;
            $(`${elms.feedbackItem}.is-active`).hide();
            $items.removeClass("is-active");
            const $nextItem = $(
                `${elms.feedbackItem}[data-id="${this.index}"]`,
            );
            $nextItem.addClass("is-active");

            $feedbackPopup.addClass("is-active");

            if (this.index === $items.length) {
                $(elms.nextButton).attr("disabled", "disabled");
                $(elms.finishfeedback).removeAttr("disabled");
            }

            if (this.index > $items.length) {
                this.hide();
                $(elms.finishfeedback).trigger("click");
            }
        }.bind(this);

        $nextButton.on("click", onCLickHandler);
    }

    feedbackNav() {
        const elms = this.elms;

        $(elms.feedbacksMark).on("click", function () {
            const allfeedbacks = $(elms.feedbackItem).length;
            const feedbackIndex = $(this).data("id");
            const transform = 100 * (feedbackIndex - 1);

            $(elms.feedbacksMark).removeClass("is-active");
            $(elms.feedbackItem).removeClass("is-active");
            $(this).addClass("is-active");
            $('.js-feedback-feed[data-id="' + feedbackIndex + '"]').addClass(
                "is-active",
            );
            $(elms.feedbacks).css({
                transform: "translateX(-" + transform + "%)",
            });

            if (allfeedbacks === feedbackIndex) {
                $(elms.nextButton).attr("disabled", "disabled");
                $(elms.finishfeedback).attr("disabled", null);
            } else {
                $(elms.nextButton).attr("disabled", null);
                $(elms.finishfeedback).attr("disabled", "disabled");
            }
        });
    }

    show() {
        const elms = this.elms;
        const blur = $(".app-blur");
        const $feedbackPopup = $(elms.feedbackPopup);
        const $feedbacks = $(elms.feedbackItem);

        // $(elms.feedbackItem).removeClass("is-active");

        if ($feedbacks.length > 1) {
            const $index = $(
                ".js-challenge-coordinate.is-active.is-finished",
            ).last();

            this.questionId = $index.attr("data-id");
        }

        let $item = $feedbackPopup.find(
            `${elms.feedbackItem}[data-question-id="${this.questionId}"]`,
        );

        if ($item.length === 0)
            $item = $feedbackPopup.find(`${elms.feedbackItem}`).last();

        const currentQuestionId = $item.attr("data-question-id");

        // if (currentQuestionId && this.hasFeedbackBeenShown(currentQuestionId))
        //     return;
        // if (currentQuestionId) return;

        $(elms.feedbackItem).hide();

        setTimeout(
            function () {
                blur.toggleClass("is-blurred", true);

                $item.show();
                $item.addClass("is-active");
                $feedbackPopup.addClass("is-open");

                if (currentQuestionId) {
                    this.markFeedbackAsShown(currentQuestionId);
                }
            }.bind(this),
            1000,
        );

        $(elms.nextButton).attr("disabled", "disabled");
        $(elms.finishfeedback).removeAttr("disabled");

        this.hideQuestionPopup();
    }

    hideQuestionPopup() {
        $(".js-multipleQuestions-question").removeClass("is-open");
        $(".js-questions-popup").removeClass("is-open");
        $(".app-blur").removeClass("is-blurred");
    }

    hide() {
        const elms = this.elms;
        const blur = $(".app-blur");
        blur.toggleClass("is-blurred", false);
        $(elms.feedbackPopup).removeClass("is-open");
    }
}
